import React, { useEffect } from 'react';
import { SUPPORTED_LANGUAGES, languageFromPath, stripLanguage } from '../i18n/languages';
import { pubText, pubPath, hasPublicTranslation, PUBLIC_STATIC_PATHS, PUBLIC_TRANSLATION_DATE, translateStructuredData } from '../i18n/publicTranslation';
import { latestModifiedDate, OPEN_GRAPH_LOCALES } from '../util/seoMetadata';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  imageAlt?: string;
  structuredData?: string[];
  canonical?: string;
  hreflang?: Array<{ lang: string; url: string }>;
  language?: string;
  datePublished?: string;
  dateModified?: string;
  noindex?: boolean;
  statusCode?: 404;
}

const SITE_URL = 'https://thefableapp.com';
const SUPPORTED_LANGUAGE_CODES = new Set([
  'ar', 'bg', 'cs', 'de', 'el', 'en', 'es', 'fi', 'fr', 'hi', 'id',
  'it', 'ja', 'ko', 'nl', 'pl', 'pt', 'ro', 'ru', 'th', 'tr', 'uk', 'vi', 'zh',
]);

const HTML_LANGUAGES: Record<string, string> = {
  ar: 'ar', bg: 'bg', cs: 'cs', de: 'de', el: 'el', en: 'en-US', es: 'es',
  fi: 'fi', fr: 'fr', hi: 'hi', id: 'id', it: 'it', ja: 'ja',
  ko: 'ko', nl: 'nl', pl: 'pl', pt: 'pt', ro: 'ro', ru: 'ru', th: 'th',
  tr: 'tr', uk: 'uk', vi: 'vi', zh: 'zh-CN',
};

const SEO: React.FC<SEOProps> = ({
  title = 'Fable: The AI Story Generator',
  description = 'Create personalized AI stories with professional narration, character voices, and videos. Be the star of your own adventure. Try free today.',
  keywords = 'ai story generator, personalized stories, children stories',
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogUrl,
  ogImage = `${SITE_URL}/fable-icon.png?v=butterfly-20260916`,
  twitterTitle,
  twitterDescription,
  twitterImage,
  imageAlt,
  structuredData = [],
  canonical,
  hreflang = [],
  language,
  datePublished,
  dateModified,
  noindex = false,
  statusCode,
}) => {
  // Auto-generate canonical URL from current path if not explicitly provided
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  const normalizedPath = pathname.replace(/\/$/, '') || '/';
  const pathLanguage = normalizedPath.split('/').filter(Boolean)[0];
  const resolvedLanguage = language || HTML_LANGUAGES[
    SUPPORTED_LANGUAGE_CODES.has(pathLanguage) ? pathLanguage : 'en'
  ];
  const pageLanguage = languageFromPath(normalizedPath);
  const translatedPage = !noindex && hasPublicTranslation(normalizedPath);
  const resolvedCanonical = translatedPage ? pubPath(canonical || `${SITE_URL}${normalizedPath}`, pageLanguage) : canonical || `${SITE_URL}${normalizedPath}`;
  const alternateLinks = translatedPage ? Object.keys(SUPPORTED_LANGUAGES).map(lang => ({lang,url:pubPath(resolvedCanonical,lang as keyof typeof SUPPORTED_LANGUAGES)})) : hreflang;
  const modified = latestModifiedDate(dateModified, translatedPage && (pageLanguage !== 'en' || PUBLIC_STATIC_PATHS.includes(stripLanguage(normalizedPath))) ? PUBLIC_TRANSLATION_DATE : undefined);
  const resolvedOgUrl = translatedPage ? resolvedCanonical : ogUrl || resolvedCanonical;
  const resolvedOgImageAlt = imageAlt || ogTitle || title;
  const resolvedTwitterImage = twitterImage || ogImage;
  const webPageStructuredData = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${resolvedCanonical}#webpage`,
    url: resolvedCanonical,
    name: pubText(title),
    description: pubText(description),
    inLanguage: resolvedLanguage,
    ...(datePublished ? { datePublished } : {}),
    ...(modified ? { dateModified: modified } : {}),
    isPartOf: {
      '@type': 'WebSite',
      '@id': 'https://thefableapp.com/#website',
      url: 'https://thefableapp.com/',
      name: 'Fable',
    },
    about: {
      '@type': 'Organization',
      '@id': 'https://thefableapp.com/#organization',
      name: 'Fable',
      url: 'https://thefableapp.com/',
    },
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: ogImage,
    },
  });

  useEffect(() => {
    document.documentElement.lang = resolvedLanguage;
    document.documentElement.dir = pageLanguage === 'ar' ? 'rtl' : 'ltr';
    document.querySelectorAll('[data-static-seo="true"]').forEach((element) => element.remove());
    document.querySelectorAll('.static-homepage-fallback').forEach((element) => element.remove());
  }, [resolvedLanguage, pageLanguage]);

  return (
    <>
      <title>{pubText(title)}</title>
      <meta name="description" content={pubText(description)} />
      <meta name="keywords" content={pubText(keywords)} />
      <meta name="author" content="Fable" />
      <meta name="application-name" content="Fable" />
      {statusCode && <meta name="prerender-status-code" content={String(statusCode)} />}
      <meta httpEquiv="content-language" content={resolvedLanguage} />

      {/* Robots directive allows large search previews. */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large" />
      )}

      {/* Canonical URL */}
      <link rel="canonical" href={resolvedCanonical} />

      {/* Language and Regional URLs - hreflang */}
      {alternateLinks.length > 0 && (
        <>
          {alternateLinks.map((item, index) => (
            <link key={index} rel="alternate" hrefLang={item.lang} href={item.url} />
          ))}
          {!alternateLinks.some((item) => item.lang === 'x-default') && (
            <link
              rel="alternate"
              hrefLang="x-default"
              href={alternateLinks.find((item) => item.lang === 'en')?.url || resolvedCanonical}
            />
          )}
        </>
      )}

      {/* Open Graph */}
      <meta property="og:title" content={pubText(ogTitle || title)} />
      <meta property="og:description" content={pubText(ogDescription || description)} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={resolvedOgUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:alt" content={pubText(resolvedOgImageAlt)} />
      <meta property="og:site_name" content="Fable" />
      <meta property="og:locale" content={OPEN_GRAPH_LOCALES[pageLanguage]} />
      {datePublished && <meta property="article:published_time" content={datePublished} />}
      {modified && <meta property="article:modified_time" content={modified} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@TheFableApp" />
      <meta name="twitter:creator" content="@BoshoffWiehan" />
      <meta name="twitter:title" content={pubText(twitterTitle || ogTitle || title)} />
      <meta name="twitter:description" content={pubText(twitterDescription || ogDescription || description)} />
      <meta name="twitter:url" content={resolvedOgUrl} />
      <meta name="twitter:image" content={resolvedTwitterImage} />
      <meta name="twitter:image:alt" content={pubText(resolvedOgImageAlt)} />

      {/* Structured Data */}
      {[webPageStructuredData, ...structuredData].map((data, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: translatedPage ? translateStructuredData(data, pageLanguage, modified) : data }}
        />
      ))}
    </>
  );
};

export default SEO;
